
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserHeader } from "./users/UserHeader";
import { UserForm, UserFormValues } from "./users/UserForm";
import { UserActions } from "./users/UserActions";
import { FeedbackAlert } from "@/components/ui/feedback-alert";
import { useAuth } from "@/hooks/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const { user, profile } = useAuth();

  const handleSubmit = async (values: UserFormValues) => {
    setIsSubmitting(true);
    setErrorDetails(null);
    
    try {
      console.log("📝 [AddUser] Création d'un utilisateur via Edge Function:", values);
      
      // Utiliser la fonction Edge pour créer l'utilisateur
      const pseudoEmail = `${values.nickname}@user.local`;
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: pseudoEmail,
          pin: values.pin,
          name: values.name,
          role: values.role,
          active: values.active,
          phone: values.phone || null
        }
      });

      if (error) {
        console.error("❌ [AddUser] Erreur fonction Edge:", error);
        throw error;
      }

      if (data?.error) {
        console.error("❌ [AddUser] Erreur dans la réponse:", data.error);
        throw new Error(data.error);
      }

      console.log("✅ [AddUser] Utilisateur créé avec succès:", data);
      toast.success(`Utilisateur "${values.name}" ajouté avec succès`);
      navigate("/gestion/users");
    } catch (error: any) {
      console.error("❌ [AddUser] Erreur détaillée:", error);
      
      let errorMessage = "Erreur inconnue";
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      setErrorDetails(errorMessage);
      toast.error(`Erreur lors de l'ajout de l'utilisateur: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col">
        <UserHeader title="Ajouter un utilisateur" />
        <main className="flex-1 p-4 pb-24">
          <FeedbackAlert
            type="error"
            title="Accès non autorisé"
            description="Vous devez être connecté pour accéder à cette page."
            className="mb-4"
            autoClose={false}
          />
        </main>
      </div>
    );
  }

  if (profile.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col">
        <UserHeader title="Ajouter un utilisateur" />
        <main className="flex-1 p-4 pb-24">
          <FeedbackAlert
            type="error"
            title="Accès non autorisé"
            description="Vous devez être administrateur pour ajouter un utilisateur."
            className="mb-4"
            autoClose={false}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex flex-col">
      <UserHeader title="Ajouter un utilisateur" />

      <main className="flex-1 p-4 pb-24">
        <UserForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        
        {errorDetails && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <h3 className="text-sm font-semibold text-red-700 mb-2">Détails de l'erreur (pour le débogage):</h3>
            <pre className="text-xs overflow-auto p-2 bg-red-100 rounded max-h-40">
              {errorDetails}
            </pre>
          </div>
        )}
      </main>

      <UserActions
        onSubmit={() => document.querySelector('form')?.requestSubmit()}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};
